import type { UserData, UserRole } from '../types/user'
import type { UserProfile } from '../core/models/User'
import type { AccountProfile, AvatarSource, UserDataProfilePatch } from './types'

const GOOGLE_HOST_FRAGMENTS = ['googleusercontent.com', 'lh3.googleusercontent.com']

export function isGoogleHostedAvatarUrl(url: string | undefined | null): boolean {
  if (!url) return false
  return GOOGLE_HOST_FRAGMENTS.some((f) => url.includes(f))
}

export function resolveAvatarSource(profile: UserProfile): AvatarSource {
  const url = (profile.customAvatarUrl || '').trim()
  if (url) {
    if (isGoogleHostedAvatarUrl(url)) return 'google'
    return 'custom_upload'
  }
  const av = (profile.avatar || '').toLowerCase()
  if (av && av !== 'custom') return 'boring_variant'
  return 'boring_variant'
}

/** Append cache-bust query so replaced uploads refresh in browsers and `<img>` caches. */
export function withAvatarCacheBust(url: string, version?: string | number | Date | null): string {
  const u = (url || '').trim()
  if (!u) return ''
  const v =
    version != null && version !== ''
      ? typeof version === 'number' || typeof version === 'string'
        ? String(version)
        : version instanceof Date
          ? String(version.getTime())
          : String(version)
      : String(Date.now())
  try {
    const parsed = new URL(u, typeof window !== 'undefined' ? window.location.origin : 'https://local.invalid')
    parsed.searchParams.set('v', v)
    // If original was relative, return path + search
    if (u.startsWith('/')) {
      return `${parsed.pathname}${parsed.search}`
    }
    return parsed.toString()
  } catch {
    const sep = u.includes('?') ? '&' : '?'
    return `${u}${sep}v=${encodeURIComponent(v)}`
  }
}

/** Build full `UserData` for Redux from auth identity + `UserProfile` (single mapping entry point). */
export function userProfileToFullUserData(
  auth: {
    id: string
    email: string
    username: string
    role: UserRole
    createdAt: string
    emailVerified?: boolean
    companyId?: string | null
  },
  profile: UserProfile,
  googleLinked?: boolean
): UserData {
  const patch = userProfileToUserDataPatch(profile)
  return {
    id: auth.id,
    email: auth.email,
    username: auth.username,
    role: auth.role,
    createdAt: auth.createdAt,
    emailVerified: auth.emailVerified,
    companyId: auth.companyId,
    ...patch,
    googleLinked: googleLinked ?? false,
  }
}

export function userProfileToUserDataPatch(profile: UserProfile): UserDataProfilePatch {
  return {
    firstName: profile.firstName ?? '',
    lastName: profile.lastName ?? '',
    displayName: profile.displayName ?? '',
    title: profile.title ?? '',
    contact: profile.contact ?? '',
    bio: profile.bio ?? '',
    avatar: profile.avatar ?? 'beam',
    gameCharacter: profile.gameCharacter ?? 'adam',
    customAvatarUrl: profile.customAvatarUrl ?? '',
    profileCompleted: profile.profileCompleted ?? false,
    gender: profile.gender ?? '',
    dateOfBirth: profile.dateOfBirth as string | Date | undefined,
    avatarSource: resolveAvatarSource(profile),
    avatarUpdatedAt:
      profile.avatarUpdatedAt != null
        ? typeof profile.avatarUpdatedAt === 'string'
          ? profile.avatarUpdatedAt
          : new Date(profile.avatarUpdatedAt).toISOString()
        : undefined,
  }
}

export const EMPTY_USER_PROFILE: UserProfile = {
  firstName: '',
  lastName: '',
  title: '',
  contact: '',
  bio: '',
  avatar: 'adam',
  gameCharacter: 'adam',
  customAvatarUrl: '',
  profileCompleted: false,
  role: 'user',
}

export function coerceUserProfile(p: Partial<UserProfile> | null | undefined): UserProfile {
  return { ...EMPTY_USER_PROFILE, ...p }
}

/** Reconstruct `UserProfile` from Redux `UserData` when Parse profile object is not in memory. */
export function userDataToUserProfile(userData: UserData): UserProfile {
  return {
    firstName: userData.firstName,
    lastName: userData.lastName,
    displayName: userData.displayName,
    title: userData.title,
    contact: userData.contact,
    bio: userData.bio,
    avatar: userData.avatar,
    gameCharacter: userData.gameCharacter,
    customAvatarUrl: userData.customAvatarUrl,
    profileCompleted: userData.profileCompleted,
    role: userData.role,
    gender: userData.gender,
    dateOfBirth: userData.dateOfBirth,
    avatarSource: userData.avatarSource,
    avatarUpdatedAt: userData.avatarUpdatedAt,
  }
}

export function mergeUserDataWithProfile(
  base: UserData,
  profile: UserProfile,
  extras?: { googleLinked?: boolean }
): UserData {
  const patch = userProfileToUserDataPatch(profile)
  return {
    ...base,
    ...patch,
    googleLinked: extras?.googleLinked ?? base.googleLinked,
  }
}

export function buildAccountProfile(args: {
  userData: UserData
  profile: UserProfile
  googleLinked?: boolean
}): AccountProfile {
  const { userData, profile, googleLinked } = args
  return {
    userId: userData.id,
    email: userData.email,
    username: userData.username,
    role: userData.role,
    createdAt: userData.createdAt,
    emailVerified: userData.emailVerified,
    profile,
    avatarSource: resolveAvatarSource(profile),
    googleLinked: googleLinked ?? Boolean(userData.googleLinked),
  }
}
